'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  ArrowRight,
  Shield,
  Users,
  BookOpen,
  GraduationCap,
  School,
  CheckCircle,
  BarChart3,
  Bell,
  ClipboardList,
  Lock,
  Globe,
  ChevronRight,
  Star,
  Layers,
  UserCheck,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


/* ─── Brand tokens ─────────────────────────────────────────────── */
const NAVY = '#1F497D';
const GREEN = '#009B77';
const GOLD = '#FDB913';
const CHARCOAL = '#1F2933';
const SOFT_GREY = '#F4F6F8';

/* ─── Animated counter hook ─────────────────────────────────────── */
function useCounter(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

/* ─── Intersection observer hook ────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

/* ─── Feature data ───────────────────────────────────────────────── */
const features = [
  { icon: School, label: 'School Organisation Registration', size: 'tall' },
  { icon: UserCheck, label: 'Admin Approval Portal', size: 'normal' },
  { icon: BarChart3, label: 'School Dashboard & Analytics', size: 'normal' },
  { icon: GraduationCap, label: 'Learner Profiles', size: 'wide' },
  { icon: BookOpen, label: 'Teacher Management', size: 'normal' },
  { icon: Users, label: 'Parent & Guardian Portals', size: 'normal' },
  { icon: ClipboardList, label: 'Attendance Registers', size: 'wide' },
  { icon: BookOpen, label: 'Homework & Lessons', size: 'normal' },
  { icon: Star, label: 'Assessments & Report Cards', size: 'normal' },
  { icon: Shield, label: 'Behaviour Tracking', size: 'normal' },
  { icon: Bell, label: 'Parent Communication', size: 'normal' },
  { icon: Layers, label: 'Performance Dashboards', size: 'normal' },
];

/* ─── Role cards ─────────────────────────────────────────────────── */
const roles = [
  {
    icon: School,
    title: 'School Management',
    color: NAVY,
    accent: '#2a5fa8',
    description:
      'View real-time school stats, learner performance, teacher activity, attendance trends, and communication records.',
    tags: ['Real-time stats', 'Attendance trends', 'Communication logs'],
  },
  {
    icon: BookOpen,
    title: 'Teachers',
    color: GREEN,
    accent: '#00b589',
    description:
      'Manage attendance, homework, assessments, lessons, learner progress, and parent messages from one dashboard.',
    tags: ['Attendance', 'Assessments', 'Homework', 'Messages'],
  },
  {
    icon: Users,
    title: 'Parents & Guardians',
    color: '#C8860A',
    accent: GOLD,
    description:
      'Monitor attendance, results, behaviour, report cards, homework, and school updates from their phone.',
    tags: ['Report cards', 'Behaviour', 'School updates'],
  },
  {
    icon: GraduationCap,
    title: 'Learners',
    color: '#0077A8',
    accent: '#00A8D4',
    description:
      'Access homework, timetables, marks, achievements, and important school updates.',
    tags: ['Homework', 'Timetables', 'Marks', 'Achievements'],
  },
  {
    icon: Shield,
    title: 'System Administrators',
    color: '#2D2D5E',
    accent: GOLD,
    description:
      'Approve school registrations, manage school organisations, and maintain secure platform access.',
    tags: ['Registrations', 'School orgs', 'Platform access'],
  },
];

/* ─── Stats ──────────────────────────────────────────────────────── */
const stats = [
  { value: 2000, suffix: '+', label: 'Schools Supported', sub: 'Built to scale across Gauteng and South Africa' },
  { value: 5, suffix: '+', label: 'Dedicated Portals', sub: 'System Admin, School Admin, Teachers, Parents & Learners' },
  { value: 100, suffix: '%', label: 'Data Isolation', sub: 'Every school operates in its own secure digital environment' },
];

/* ─── Stat card ──────────────────────────────────────────────────── */
function StatCard({ value, suffix, label, sub, start }: { value: number; suffix: string; label: string; sub: string; start: boolean }) {
  const count = useCounter(value, 1600, start);
  return (
    <div className="flex flex-col items-center text-center px-6 py-8">
      <span className="text-5xl font-extrabold" style={{ color: GOLD, fontVariantNumeric: 'tabular-nums' }}>
        {count.toLocaleString()}{suffix}
      </span>
      <p className="mt-2 text-lg font-bold text-white">{label}</p>
      <p className="mt-1 text-sm text-blue-200 max-w-[220px]">{sub}</p>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
export default function LandingPage() {
  const statsSection = useInView(0.3);
  const featuresSection = useInView(0.1);
  const rolesSection = useInView(0.1);
  const ctaSection = useInView(0.2);

  return (
    <div className="min-h-screen font-sans" style={{ color: CHARCOAL }}>

      {/* ── NAV ─────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-white/10" style={{ backgroundColor: `${NAVY}f2` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: GOLD }}>
              <GraduationCap size={18} color={NAVY} />
            </div>
            <span className="font-extrabold text-white text-lg tracking-tight">LearnerLink SA</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {['Features', 'Portals', 'Security', 'Stats'].map((item) => (
              <a key={item} href={`#${item.toLowerCase()}`} className="text-blue-200 hover:text-white text-sm font-medium transition-colors">
                {item}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Link href="/sign-up-login-screen" className="text-sm font-semibold text-blue-200 hover:text-white transition-colors hidden sm:block">
              Sign In
            </Link>
            <Link
              href="/school-registration-page"
              className="text-sm font-bold px-4 py-2 rounded-lg transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: GOLD, color: NAVY }}
            >
              Register School
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16" style={{ backgroundColor: NAVY }}>
        {/* Hero background image */}
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src="/assets/images/hero_school_campus_sa.png"
            alt="South African school campus with students and modern digital connectivity"
            fill
            className="object-cover object-center opacity-20"
            priority
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${NAVY}ee 0%, ${NAVY}cc 50%, ${NAVY}99 100%)` }} />
          <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${GREEN} 0%, transparent 70%)` }} />
          <div className="absolute -bottom-48 -left-24 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }} />
          {/* Grid lines */}
          <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 w-full">
          <div className="max-w-2xl xl:max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-8 border border-white/20 backdrop-blur-sm" style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: GOLD }}>
              <Globe size={12} />
              South African Cloud-Based School Management
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.05] tracking-tight mb-6">
              One Secure Platform<br />
              <span style={{ color: GOLD }}>for Every School</span>
            </h1>

            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed mb-4 max-w-2xl">
              A South African cloud-based digital transformation operating system built to connect school management, teachers, learners, and parents in one powerful system.
            </p>

            <p className="text-sm font-semibold tracking-widest mb-10" style={{ color: GREEN }}>
              CONNECT • MANAGE • EMPOWER
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/school-registration-page"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all hover:opacity-90 active:scale-95 shadow-lg"
                style={{ backgroundColor: GOLD, color: NAVY }}
              >
                Register Your School
                <ArrowRight size={18} />
              </Link>
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base border-2 border-white/30 text-white hover:bg-white/10 transition-all active:scale-95"
              >
                Book a Demo
                <ChevronRight size={18} />
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-6 mt-12 pt-8 border-t border-white/10">
              {[
                { icon: Shield, text: '100% Data Isolation' },
                { icon: Lock, text: 'Dept. of Basic Education Compliant' },
                { icon: Globe, text: 'All 9 Provinces' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-blue-200 text-sm">
                  <Icon size={14} style={{ color: GREEN }} />
                  {text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating portal cards — only on very wide screens to avoid overlap */}
        <div className="hidden 2xl:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col gap-3 z-10">
          {[
            { label: 'System Admin', color: '#2D2D5E', accent: GOLD },
            { label: 'School Admin', color: NAVY, accent: GREEN },
            { label: 'Teacher Portal', color: GREEN, accent: '#fff' },
            { label: 'Parent Portal', color: '#C8860A', accent: '#fff' },
            { label: 'Learner Portal', color: '#0077A8', accent: '#fff' },
          ].map((p, i) => (
            <div
              key={p.label}
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 backdrop-blur-sm"
              style={{
                backgroundColor: `${p.color}cc`,
                transform: `translateX(${i % 2 === 0 ? '0' : '12px'})`,
                transition: 'transform 0.3s ease',
              }}
            >
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.accent }} />
              <span className="text-white text-xs font-semibold whitespace-nowrap">{p.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROBLEM STATEMENT ───────────────────────────────────── */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: SOFT_GREY }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6 border" style={{ color: NAVY, borderColor: `${NAVY}30`, backgroundColor: `${NAVY}08` }}>
                The Challenge
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-6" style={{ color: NAVY }}>
                Bridging the Gap in<br />School Management
              </h2>
              <p className="text-base leading-relaxed mb-6" style={{ color: '#4A5568' }}>
                LearnerLink SA helps schools move away from paperwork, disconnected communication, manual attendance, and scattered learner records.
              </p>
              <p className="text-base leading-relaxed" style={{ color: '#4A5568' }}>
                It brings everything into one secure digital platform designed for South African schools, principals, teachers, parents, and learners.
              </p>
            </div>

            {/* Before/After image + pain points */}
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { before: 'Paper registers', after: 'Digital attendance' },
                  { before: 'Scattered records', after: 'Unified learner profiles' },
                  { before: 'Manual reports', after: 'Auto-generated report cards' },
                  { before: 'Missed messages', after: 'Real-time parent alerts' },
                ].map(({ before, after }) => (
                  <div key={before} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-500">Before</span>
                    </div>
                    <p className="text-sm font-medium text-gray-500 line-through mb-2">{before}</p>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${GREEN}15`, color: GREEN }}>After</span>
                    </div>
                    <p className="text-sm font-bold" style={{ color: NAVY }}>{after}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES BENTO ──────────────────────────────────────── */}
      <section id="features" className="py-20 lg:py-28 bg-white" ref={featuresSection.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4 border" style={{ color: GREEN, borderColor: `${GREEN}30`, backgroundColor: `${GREEN}08` }}>
              Everything in One System
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold" style={{ color: NAVY }}>
              Every tool your school needs
            </h2>
          </div>

          {/* Responsive bento grid */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
            style={{
              opacity: featuresSection.inView ? 1 : 0,
              transform: featuresSection.inView ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            {/* Large card — spans 2 cols on xl, full width on smaller */}
            <div className="sm:col-span-2 xl:col-span-2 xl:row-span-2 rounded-3xl p-8 flex flex-col justify-between min-h-[200px] xl:min-h-[240px]" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2a5fa8 100%)` }}>
              <School size={36} color={GOLD} />
              <div>
                <h3 className="text-xl font-bold text-white mb-2">School Organisation Registration</h3>
                <p className="text-blue-200 text-sm">Register your school, get approved, and go live in minutes with full data isolation from day one.</p>
              </div>
            </div>

            {/* Normal cards */}
            {[
              { icon: UserCheck, label: 'Admin Approval Portal', desc: 'System admins review and approve school registrations.' },
              { icon: BarChart3, label: 'School Dashboard', desc: 'Real-time KPIs, charts, and performance at a glance.' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="rounded-3xl p-6 flex flex-col gap-3 border border-gray-100 hover:shadow-md transition-shadow" style={{ backgroundColor: SOFT_GREY }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${NAVY}12` }}>
                  <Icon size={20} style={{ color: NAVY }} />
                </div>
                <div>
                  <h3 className="font-bold text-sm mb-1" style={{ color: NAVY }}>{label}</h3>
                  <p className="text-xs text-gray-500">{desc}</p>
                </div>
              </div>
            ))}

            {/* Wide card — spans 2 cols on xl */}
            <div className="sm:col-span-2 xl:col-span-2 rounded-3xl p-6 flex items-center gap-6 border border-gray-100" style={{ backgroundColor: `${GREEN}08` }}>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: GREEN }}>
                <GraduationCap size={24} color="white" />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ color: NAVY }}>Learner Profiles</h3>
                <p className="text-sm text-gray-500">Complete academic history, attendance, behaviour, and results in one profile.</p>
              </div>
            </div>

            {/* More cards */}
            {[
              { icon: ClipboardList, label: 'Attendance Registers', color: NAVY },
              { icon: BookOpen, label: 'Homework & Lessons', color: GREEN },
              { icon: Star, label: 'Assessments & Report Cards', color: '#C8860A' },
              { icon: Shield, label: 'Behaviour Tracking', color: '#C8232A' },
              { icon: Bell, label: 'Parent Communication', color: '#0077A8' },
              { icon: Layers, label: 'Performance Dashboards', color: NAVY },
            ].map(({ icon: Icon, label, color }) => (
              <div key={label} className="rounded-3xl p-5 flex flex-col gap-3 border border-gray-100 hover:shadow-md transition-shadow bg-white">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
                  <Icon size={18} style={{ color }} />
                </div>
                <p className="font-semibold text-sm" style={{ color: CHARCOAL }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ROLES ───────────────────────────────────────────────── */}
      <section id="portals" className="py-20 lg:py-28" style={{ backgroundColor: SOFT_GREY }} ref={rolesSection.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4 border" style={{ color: NAVY, borderColor: `${NAVY}30`, backgroundColor: `${NAVY}08` }}>
              Built for Every Role
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold" style={{ color: NAVY }}>
              A dedicated portal for everyone
            </h2>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
            style={{
              opacity: rolesSection.inView ? 1 : 0,
              transform: rolesSection.inView ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            {roles.map(({ icon: Icon, title, color, accent, description, tags }) => (
              <div
                key={title}
                className="rounded-3xl overflow-hidden border border-white/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Card header */}
                <div className="p-6 pb-4" style={{ background: `linear-gradient(135deg, ${color} 0%, ${accent}99 100%)` }}>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 bg-white/20">
                    <Icon size={24} color="white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{title}</h3>
                </div>
                {/* Card body */}
                <div className="bg-white p-6">
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">{description}</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span key={tag} className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${color}10`, color }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECURITY ────────────────────────────────────────────── */}
      <section id="security" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Visual */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <Image
                  src="/assets/images/security_multi_school_isolation.png"
                  alt="Multi-school data isolation diagram showing each school connected to a central secure cloud platform with individual shield protection"
                  width={600}
                  height={480}
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Overlay badge */}
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl p-4 backdrop-blur-sm border border-white/20" style={{ backgroundColor: `${NAVY}dd` }}>
                <p className="text-center text-xs font-semibold tracking-wider" style={{ color: GOLD }}>
                  ONE PLATFORM · MANY SCHOOLS · NO SHARED DATA
                </p>
              </div>
            </div>

            {/* Text */}
            <div>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6 border" style={{ color: NAVY, borderColor: `${NAVY}30`, backgroundColor: `${NAVY}08` }}>
                Data Security
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-6" style={{ color: NAVY }}>
                Your school's data stays<br />
                <span style={{ color: GREEN }}>completely yours</span>
              </h2>
              <p className="text-base leading-relaxed mb-8 text-gray-600">
                One platform. Many schools. No shared data between organisations. Every school operates in its own secure digital environment with row-level security enforced at the database level.
              </p>
              <div className="space-y-4">
                {[
                  'Row-level security on every database table',
                  'School data never crosses organisational boundaries',
                  'Role-based access for every user type',
                  'Audit logs for all critical actions',
                  'Department of Basic Education compliant',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle size={18} style={{ color: GREEN }} className="mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ───────────────────────────────────────────────── */}
      <section id="stats" className="py-20 lg:py-28" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0d2a52 100%)` }} ref={statsSection.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Trusted across South Africa
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {stats.map((s) => (
              <StatCard key={s.label} {...s} start={statsSection.inView} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────────── */}
      <section className="py-24 lg:py-32 bg-white" ref={ctaSection.ref}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            style={{
              opacity: ctaSection.inView ? 1 : 0,
              transform: ctaSection.inView ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2a5fa8 100%)` }}>
              <GraduationCap size={32} color={GOLD} />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-6" style={{ color: NAVY }}>
              Ready to Digitally Transform<br />
              <span style={{ color: GREEN }}>Your School?</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
              LearnerLink SA gives your school the tools to connect, manage, report, and empower every learner from one secure platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/school-registration-page"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all hover:opacity-90 active:scale-95 shadow-lg"
                style={{ backgroundColor: NAVY, color: 'white' }}
              >
                Register Your School Today
                <ArrowRight size={18} />
              </Link>
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base border-2 transition-all active:scale-95 hover:bg-gray-50"
                style={{ borderColor: NAVY, color: NAVY }}
              >
                Book a Live Demo
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────── */}
      <footer className="py-10 border-t border-gray-100" style={{ backgroundColor: SOFT_GREY }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: NAVY }}>
              <GraduationCap size={14} color={GOLD} />
            </div>
            <span className="font-bold text-sm" style={{ color: NAVY }}>LearnerLink SA</span>
          </div>
          <p className="text-xs text-gray-400 text-center">
            © 2026 LearnerLink SA · Department of Basic Education Compliant · All 9 Provinces
          </p>
          <div className="flex items-center gap-4">
            <Link href="/sign-up-login-screen" className="text-xs text-gray-500 hover:text-gray-700 transition-colors">Sign In</Link>
            <Link href="/school-registration-page" className="text-xs font-semibold transition-colors hover:opacity-80" style={{ color: NAVY }}>Register School</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
